import { isBlocked, STORY_STEP, STORY_STEPS_PER_SECOND, type CollisionGrid, type StoryDirection } from '@rdd/contracts'

/**
 * O personagem andando na grade (docs/21 secoes 5 e 6). Logica pura: o desenho le daqui a posicao
 * interpolada, e o teste exercita sem canvas.
 *
 * Um passo dura `1000 / STORY_STEPS_PER_SECOND` ms -- o mesmo teto que o servidor aplica (modo_historia/04),
 * entao andar no ritmo da tela nunca e recusado por excesso. Durante o passo o personagem desliza do tile de
 * origem ao de destino, com um salto leve: o sprite da v1 tem um quadro so (docs/21 secao 6).
 *
 * Passo contra parede nao anda: so vira para o lado pedido.
 */

export const STEP_MS = 1000 / STORY_STEPS_PER_SECOND

export type WalkerView = {
  /** Posicao em tiles, fracionaria no meio do passo. */
  x: number
  y: number
  /** Altura do salto, de 0 a 1. */
  hop: number
  facing: StoryDirection
}

export class Walker {
  private fromX: number
  private fromY: number
  private elapsed = STEP_MS
  private queue: StoryDirection[] = []
  facing: StoryDirection = 'down'

  constructor(
    private readonly grid: CollisionGrid,
    public tileX: number,
    public tileY: number,
  ) {
    this.fromX = tileX
    this.fromY = tileY
  }

  get moving(): boolean {
    return this.elapsed < STEP_MS
  }

  /** Troca o caminho inteiro (o toque novo substitui o anterior). */
  follow(path: readonly StoryDirection[]): void {
    this.queue = [...path]
  }

  /** Um passo so (teclado). Substitui o que estava na fila. */
  walk(dir: StoryDirection): void {
    this.queue = [dir]
  }

  stop(): void {
    this.queue = []
  }

  /**
   * Avanca o tempo. Devolve as direcoes dos passos que COMECARAM neste quadro -- e o que o servidor recebe
   * em modo_historia/04.
   */
  update(deltaMs: number): StoryDirection[] {
    const started: StoryDirection[] = []
    this.elapsed += deltaMs
    while (this.elapsed >= STEP_MS && this.queue.length > 0) {
      const dir = this.queue.shift()
      if (dir === undefined) break
      this.facing = dir
      const nx = this.tileX + STORY_STEP[dir].dx
      const ny = this.tileY + STORY_STEP[dir].dy
      if (isBlocked(this.grid, nx, ny)) {
        this.queue = []
        break
      }
      // O que sobrou do passo anterior entra no seguinte: o ritmo nao deriva com quadros irregulares.
      this.elapsed = Math.min(this.elapsed - STEP_MS, STEP_MS - 1)
      this.fromX = this.tileX
      this.fromY = this.tileY
      this.tileX = nx
      this.tileY = ny
      started.push(dir)
    }
    if (this.queue.length === 0 && this.elapsed > STEP_MS) this.elapsed = STEP_MS
    return started
  }

  view(): WalkerView {
    const t = Math.min(1, this.elapsed / STEP_MS)
    return {
      x: this.fromX + (this.tileX - this.fromX) * t,
      y: this.fromY + (this.tileY - this.fromY) * t,
      hop: this.moving ? Math.sin(Math.PI * t) : 0,
      facing: this.facing,
    }
  }

  /** Coloca o personagem num tile, sem passo (nascimento, correcao do servidor). */
  place(x: number, y: number): void {
    this.tileX = x
    this.tileY = y
    this.fromX = x
    this.fromY = y
    this.elapsed = STEP_MS
    this.queue = []
  }
}
